import React, { useEffect, useRef } from 'react'

import {
  Body2,
  Box,
  Button,
  ButtonColorVariant,
  ButtonSize,
  Color,
  Display,
  FlexDirection,
  FontWeight,
  GridDisplay,
  Overflow,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { CarouselTrack } from '../RecipeCarousel/CarouselTrack'
import {
  RecipeCarouselCard,
  type RecipeCarouselCardOptions,
} from '../RecipeCarousel/RecipeCarouselCard'

export type HeatAndEatMeal = {
  id: string
  title: string
  imageUrl: string
  imageAlt: string
  cookTimeMins?: number
  kcal?: number
  ariaLabel?: string
  onClick: () => void
}

export type HeatAndEatCarouselCta = {
  title: string
  onClick: () => void
}

export type HeatAndEatCarouselProps = {
  tag: string
  title: string
  subtitle?: string
  meals: HeatAndEatMeal[]
  primaryAction: HeatAndEatCarouselCta
  secondaryAction?: HeatAndEatCarouselCta
  // Fired once when the section mounts (one impression per dashboard load, like
  // the page-load event) — the renderer wires this to the displayAnalytics event.
  onDisplayed?: () => void
}

// Heat & Eat renders the shared card at its own dimensions with a "•" meta separator.
const CARD_OPTIONS: RecipeCarouselCardOptions = {
  widths: { base: '228px', medium: '237px', large: '291px' },
  imageHeight: '155px',
  metaSeparator: ' • ',
}

// Category tag per Figma: warning-inverted background (Warning_900), white text,
// flush-left with right corners rounded. Uppercased via styling so the rendered
// text stays the xAPI-supplied value ("Heat & Eat").
const Tag = withCitrusTheme('span', (theme) => ({
  alignSelf: 'flex-start',
  display: 'inline-block',
  backgroundColor: theme.color(Color.Warning_900),
  color: theme.color(Color.White),
  borderTopRightRadius: '4px',
  borderBottomRightRadius: '4px',
  padding: `${theme.spacing(1)}px ${theme.spacing(3)}px`,
  textTransform: 'uppercase',
}))

// Image midpoint (155px tall image) so the nav arrows overlay the image centre
// regardless of the taller card.
const NAV_OFFSET = { top: '77px', transform: 'translateY(-50%)' }

export const HeatAndEatCarousel = ({
  tag,
  title,
  subtitle,
  meals,
  primaryAction,
  secondaryAction,
  onDisplayed,
}: HeatAndEatCarouselProps) => {
  const hasFiredDisplayed = useRef(false)
  useEffect(() => {
    if (!hasFiredDisplayed.current) {
      hasFiredDisplayed.current = true
      onDisplayed?.()
    }
  }, [onDisplayed])

  return (
    // Elevated white card per Figma (Elevation/50); 8px radius isn't a citrus radius token.
    <Box
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      gap={3}
      paddingV={3}
      bg={Color.White}
      boxShadow="0px 1px 4px 0px rgba(33, 41, 48, 0.1), 0px 0px 2px 0px rgba(33, 41, 48, 0.05)"
      overflowX={Overflow.Hidden}
      overflowY={Overflow.Hidden}
      style={{ borderRadius: '8px' }}
    >
      <Tag>
        <Text size={1}>{tag}</Text>
      </Tag>

      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        gap={1}
        paddingH={[4, null, null, 8]}
      >
        <Body2 color={Color.ColdGrey_900} fontWeight={FontWeight.Bold} size={[5, 9]}>
          {title}
        </Body2>
        {subtitle ? (
          <Text color={Color.ColdGrey_900} size={1}>
            {subtitle}
          </Text>
        ) : null}
      </Box>

      <CarouselTrack
        ariaLabel={title}
        items={meals}
        renderItem={(meal) => <RecipeCarouselCard recipe={meal} options={CARD_OPTIONS} />}
        navOffset={NAV_OFFSET}
        paddingH={[4, null, null, 8]}
      />

      {/* Grid keeps each CTA a fixed 48px tall; stacks, then 50/50 from desktop. */}
      <Box
        display={GridDisplay.Grid}
        gridTemplateColumns={secondaryAction ? ['1fr', null, null, '1fr 1fr'] : '1fr'}
        gap={[3, null, null, 4]}
        paddingV={2}
        paddingH={[4, null, null, 8]}
      >
        <Button
          type="button"
          colorVariant={ButtonColorVariant.Primary}
          size={ButtonSize.Large}
          width="100%"
          height="48px"
          onClick={primaryAction.onClick}
        >
          {primaryAction.title}
        </Button>
        {secondaryAction ? (
          <Button
            type="button"
            colorVariant={ButtonColorVariant.Secondary}
            size={ButtonSize.Large}
            width="100%"
            height="48px"
            onClick={secondaryAction.onClick}
          >
            {secondaryAction.title}
          </Button>
        ) : null}
      </Box>
    </Box>
  )
}
